import { CALC_METHOD_BY_COUNTRY, DEFAULT_SETTINGS } from "./constants";
import type { Location, Settings } from "./types";

function clampAdjustment(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(-2, Math.min(2, Math.trunc(value)));
}

export function normalizeSettings(input: Partial<Settings> | null | undefined): Settings {
  return {
    methodId: Number.isFinite(input?.methodId) ? Number(input?.methodId) : DEFAULT_SETTINGS.methodId,
    calculationMethodMode: input?.calculationMethodMode === "manual" ? "manual" : "auto",
    madhhab: input?.madhhab === "hanafi" ? "hanafi" : "shafi",
    hijriAdjustment: clampAdjustment(typeof input?.hijriAdjustment === "number" ? input.hijriAdjustment : DEFAULT_SETTINGS.hijriAdjustment),
    hijriAdjustmentMode: input?.hijriAdjustmentMode === "manual" ? "manual" : "auto",
    timeFormat: input?.timeFormat === "12h" ? "12h" : "24h",
    fardBuffers: {
      fajr: clampBuffer(input?.fardBuffers?.fajr, DEFAULT_SETTINGS.fardBuffers.fajr),
      asr: clampBuffer(input?.fardBuffers?.asr, DEFAULT_SETTINGS.fardBuffers.asr)
    }
  };
}

function clampBuffer(value: unknown, fallback: number): number {
  const n = typeof value === "number" && Number.isFinite(value) ? Math.round(value) : fallback;
  return Math.max(0, Math.min(90, n));
}

export function getAutoHijriProfile(_location: Partial<Location> | null | undefined) {
  return { adjustment: 0, label: "Standard Hijri calendar", source: "default" as const };
}

export function getEffectiveHijriAdjustment(settings: Settings, location: Partial<Location> | null | undefined): number {
  if (settings.hijriAdjustmentMode === "manual") return clampAdjustment(settings.hijriAdjustment);
  return getAutoHijriProfile(location).adjustment;
}

export function getEffectiveSettings(settings: Settings, location: Partial<Location> | null | undefined): Settings {
  const country = location?.country?.trim() ?? "";
  const regionalMethod = country ? CALC_METHOD_BY_COUNTRY[country] : undefined;
  const methodId = settings.calculationMethodMode === "manual" ? settings.methodId : regionalMethod?.id ?? settings.methodId;

  return {
    ...settings,
    methodId,
    hijriAdjustment: getEffectiveHijriAdjustment(settings, location)
  };
}

export function describeCalculationMethod(settings: Settings, location: Partial<Location> | null | undefined): string {
  const country = location?.country?.trim() ?? "";
  const regionalMethod = country ? CALC_METHOD_BY_COUNTRY[country] : undefined;
  if (settings.calculationMethodMode === "manual") return "Manual calculation method";
  return regionalMethod ? `Auto by city · ${regionalMethod.label}` : "Auto by city · MWL fallback";
}

export function describeHijriSetting(settings: Settings, location: Partial<Location> | null | undefined): string {
  const effective = getEffectiveHijriAdjustment(settings, location);
  if (settings.hijriAdjustmentMode === "manual") {
    return `Manual ${effective > 0 ? `+${effective}` : String(effective)} day`;
  }
  return "Auto · Standard Hijri calendar (0 day adjustment)";
}
